import {
  HabitSchedule,
  ScheduleEntryState,
  ScheduleTargetEntry,
  Timestamp,
} from '@griply/types';
import { addWeeks, eachDayOfInterval } from 'date-fns';

import { getTimeframeOccurrences } from './get-timeframe-occurrences';
import { isBetween } from './lib/date-fns';
import { getResolvedScheduleDays } from './schedules/get-resolved-schedule-days';

export type GetTimeframeDateEntriesOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export type TimeframeDateEntries = {
  /** The padded range — the month view renders entries either side of the timeframe. */
  dateEntries: ScheduleTargetEntry[];
  /** Only the ones inside the asked-for range. */
  timeframeDateEntries: ScheduleTargetEntry[];
};

const PADDING_WEEKS = 5;

// One entry per day over the padded range, resolved against the schedule that owns each day.
export const getTimeframeDateEntries = (
  schedules: HabitSchedule[],
  { startDate, endDate }: GetTimeframeDateEntriesOptions,
): TimeframeDateEntries => {
  const rangeStartDate = addWeeks(startDate, -PADDING_WEEKS);
  const rangeEndDate = addWeeks(endDate, PADDING_WEEKS);

  const { occurrences } = getTimeframeOccurrences(schedules, {
    startDate: rangeStartDate,
    endDate: rangeEndDate,
  });

  const targetEntries = getResolvedScheduleDays({
    schedules,
    days: eachDayOfInterval({ start: rangeStartDate, end: rangeEndDate }),
    occurrences,
    fallbackEndDate: rangeEndDate,
  }).map<ScheduleTargetEntry>((day) => ({
    date: day.date,
    id: day.entryId ?? `${day.date.getTime()}`,
    state: day.state ?? ScheduleEntryState.Complete,
    value: day.value,
    hidden: day.hidden ?? undefined,
    target: day.target,
    onDateManual: day.onDateManual,
    showValueAsBadge: (day.value ?? 0) > 1,
  }));

  return {
    dateEntries: targetEntries.filter(({ date }) =>
      isBetween(date, rangeStartDate, rangeEndDate),
    ),
    timeframeDateEntries: targetEntries.filter(({ date }) =>
      isBetween(date, startDate, endDate),
    ),
  };
};
